import { useModal } from "@ebay/nice-modal-react";
import { useCallback, useEffect } from "react";

import { useDrawerVisibilityForId } from "ds/components/DrawerNew/useDrawerVisibilityForId";
import { StackDependency, StackDependencyDetail } from "types/generated";
import DependencyItem from "views/Stack/components/DependencyItem";
import { AccessResult } from "utils/access";

import { showDependenciesDeleteConfirmationModal } from "../DeleteConfirmationModal";
import { OutputReferencesDrawer, showOutputReferencesDrawer } from "../OutputReferencesDrawer";

type DependenciesColumnItemProps = {
  dependency: StackDependency;
  stackIdSourceOfOutputs: string;
  itemStack: StackDependencyDetail;
  relatedStack: StackDependencyDetail;
  onDelete?: (dependencyId: string) => void;
  canManage: AccessResult;
  isOutputReferencesDisallowed?: boolean;
  disallowedVendorName?: string;
};

const DependenciesColumnItem = ({
  dependency,
  itemStack,
  stackIdSourceOfOutputs,
  relatedStack,
  onDelete,
  canManage,
  isOutputReferencesDisallowed,
  disallowedVendorName,
}: DependenciesColumnItemProps) => {
  const isFocused = useDrawerVisibilityForId(dependency.id);

  const drawer = useModal(OutputReferencesDrawer);

  const handleDeleteDependency = useCallback(() => {
    onDelete?.(dependency.id);
    if (isFocused) {
      drawer.hide();
    }
  }, [dependency.id, drawer, isFocused, onDelete]);

  const handleDeleteDependencyConfirmation = () => {
    showDependenciesDeleteConfirmationModal({
      onConfirm: handleDeleteDependency,
      itemStack,
      relatedStack,
    });
  };

  const handleOutputReferencesEdit = () => {
    showOutputReferencesDrawer({
      id: dependency.id,
      stackDependency: dependency,
      stackIdSourceOfOutputs,
    });
  };

  useEffect(() => {
    if (isFocused && canManage.result) {
      showOutputReferencesDrawer({
        id: dependency.id,
        stackDependency: dependency,
        stackIdSourceOfOutputs,
      });
    }
    // Re-render drawer content when dependency changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependency, stackIdSourceOfOutputs]);

  return (
    <DependencyItem
      item={itemStack}
      onDelete={onDelete ? handleDeleteDependencyConfirmation : undefined}
      onOutputReferencesEdit={handleOutputReferencesEdit}
      referenceCount={dependency.referenceCount}
      isFocused={isFocused}
      isOutputReferencesDisallowed={isOutputReferencesDisallowed}
      disallowedVendorName={disallowedVendorName}
      canManage={canManage}
    />
  );
};

export default DependenciesColumnItem;
